import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import { Core } from '../core/Core';
import { TickTimeProperties } from '../core/TickService';
import { CharacterService } from './CharacterService';
import { generateUUID } from 'three/src/math/MathUtils.js';
import { Subject, Subscription } from 'rxjs';
import { TweenService } from '../core/TweenService';
import { BridgeActionResult, BridgeErrorStrings } from '../data/BridgeData';
import { AnimationProgressEvent, AnimationProgressEventType, CharacterAnimationRegistration, CharacterAnimationDefinition, AnimationActivationInterface, AnimationDeactivationInterface, AnimationFadeInterface } from '../data/AnimationData';

// Type Definition to add the parentCharacterAnimationTitle property to AnimationActions, to reference parentage properly
declare module 'three' {
	interface AnimationAction {
		parentCharacterAnimationTitle?: any; // Replace 'any' with your desired type
		parentCharacterAnimationId?: any; // Replace 'any' with your desired type
	}
}

export class CharacterAnimatorService {
	/**
	 * Core Properties
	 */
	core: Core;

	/**
	 * Animation properties
	 */
	characterAnimationMixer?: THREE.AnimationMixer;
	characterActiveanimationTitle?: string;
	characterAnimations: CharacterAnimationDefinition[] = [];
	characterActiveAnimations: CharacterAnimationDefinition[] = [];

	/**
	 * Reverse Animation properties
	 */
	$progressEvents: Subject<AnimationProgressEvent>;
	$progressEventsListener?: Subscription;
	reversedIdAddon: string = " Reversed";
	reverseAnimationApproachTimeMS: number = 250; // The MS from the end to start blending
	reverseAnimationReturnTimeMS: number = 300; // The MS from the beginning to start blending back

	/**
	 * Constructor
	 * @param core Reference to the Core
	 * @param character Reference to the Character
	 */
	constructor(core: Core) {
		this.core = core;

		// Listen to the subject
		this.$progressEvents = new Subject();
		this.$progressEventsListener = this.$progressEvents.subscribe(this.animationPlaybackListener.bind(this));

		// Kick off animation callbacks
		this.core.s('Tick').registerCallback({ name: 'character_animation', callback: this.characterAnimationTick.bind(this) });
	}

	/**
	 * Applies the character animation mixer
	 */
	setupCharacterAnimationMixer() {
		const sCharacter: CharacterService = this.core.s('Character');
		this.characterAnimationMixer = new THREE.AnimationMixer(sCharacter.characterScene!);
		this.characterAnimationMixer.addEventListener('loop', (value: any) => this.$progressEvents.next({ type: value.type, animationAction: value.action }));
		this.characterAnimationMixer.addEventListener('finished', (value: any) => this.$progressEvents.next({ type: value.type, animationAction: value.action }));
	}

	/**
	 * When a playback event is fired through, such as approaching (reverse animation blend)
	 * @param payload In the format { type: number }
	 */
	animationPlaybackListener(payload: any) {
		switch (payload.type) {
			case AnimationProgressEventType.approaching:
				const approachingAnimation = this.characterAnimations.find(anim => anim.id == payload.animationAction.parentCharacterAnimationId);
				if (approachingAnimation) {
					this.fadeAnimation({ id: payload.animationAction.parentCharacterAnimationId, actionSelector: "reversedAction", from: 0, to: 1, fadeTimeMS: this.reverseAnimationApproachTimeMS });
				}
				break;
			case AnimationProgressEventType.loop:
				const characterAnimation = this.characterAnimations.find(anim => anim.id == payload.animationAction.parentCharacterAnimationId);
				if (characterAnimation && characterAnimation.id !== "Idle") {
					characterAnimation.autoBlendTriggered = false;

					if (characterAnimation.reversedAction) {
						this.fadeAnimation({ id: payload.animationAction.parentCharacterAnimationId, actionSelector: "reversedAction", from: 1, to: 0, fadeTimeMS: this.reverseAnimationReturnTimeMS });
					}
				}
				break;
		}
	}

	/**
	 * Sets the character to idling
	 */
	idle(): BridgeActionResult {
		const fadeBackMS = 400;
		const idleAnimation = this.getAnimationByID('Idle');
		const idleAlreadyPlaying = idleAnimation && idleAnimation.action.isRunning();

		// Play idle, if we can
		if (!idleAlreadyPlaying) this.activateAnimation({ id: "Idle", weight: 1, fadeTimeMS: fadeBackMS });

		let bridgeActionResult = new BridgeActionResult(true);
		this.characterAnimations.forEach(anim => {
			if (anim.id != "Idle" && anim.action.isRunning()) {
				const deactivationResult: BridgeActionResult = this.deactivateAnimation({ id: anim.id, fadeTimeMS: fadeBackMS });
				if (!deactivationResult.wasSuccessful) bridgeActionResult = deactivationResult;
			}
		});

		return bridgeActionResult;
	}

	/**
	 * Registers an animation
	 */
	registerAnimation(animation: CharacterAnimationRegistration, idling: boolean = false): BridgeActionResult {
		try {
			const animationClip = THREE.AnimationClip.parse(animation.json);
			const animationAction = this.characterAnimationMixer?.clipAction(animationClip)!;
			animationAction.parentCharacterAnimationId = animation.id;
			let characterAnimation: CharacterAnimationDefinition = {
				id: animation.id,
				autoBlend: animation.autoBlend,
				idle: idling,
				json: animation.json,
				clip: animationClip,
				action: this.characterAnimationMixer?.clipAction(animationClip)!,
				playbackSpeed: animation.playbackSpeed || 1
			}

			if (animation.autoBlend) {
				characterAnimation = { ...characterAnimation, ...this.createReversedClipAndAction(characterAnimation) };
			}

			this.characterAnimations.push(characterAnimation);
			return new BridgeActionResult(true);
		} catch(e) {
			return new BridgeActionResult(false, BridgeErrorStrings.register_animation_default);
		}
	}

	/**
	 * De-registers an animation
	 */
	deregisterAnimation(animationID: string): BridgeActionResult {
		const characterAnimations = this.characterAnimations.filter(anim => anim.id == animationID);
		if (!characterAnimations.length) {
			return new BridgeActionResult(false, BridgeErrorStrings.no_asset);
		}

		try {
			let shouldIdle: boolean = false;
			characterAnimations.forEach(anim => {
				if (anim.action.isRunning()) shouldIdle = true;
				if (anim.clip) this.characterAnimationMixer?.uncacheClip(anim.clip);
				if (anim.reversedClip) this.characterAnimationMixer?.uncacheClip(anim.reversedClip);
				this.characterAnimations = this.characterAnimations.filter(storedAnim => storedAnim.id !== anim.id);
			});

			if (shouldIdle) {
				this.idle();
			}

			return new BridgeActionResult(true);
		} catch(e) {
			return new BridgeActionResult(false, BridgeErrorStrings.deregister_animation_default);
		}
	}

	/**
	 * Activates an animation, by default with 0 weight.  Does not automatically activate the reverse animation, due to timing
	 * @param animationTitle string, name of the animation
	 * @param weight Number, the weight of the animation
	 * @param fadeTime The time to fade in.  If 0, will play immediately
	 */
	activateAnimation({ id, weight = 0, fadeTimeMS = 0, loopTimes = Infinity }: AnimationActivationInterface): BridgeActionResult {
		const characterAnimation = this.getAnimationByID(id);
		if (!characterAnimation) {
			return new BridgeActionResult(false, BridgeErrorStrings.no_asset);
		}

		try {
			characterAnimation.autoBlendTriggered = false;
			characterAnimation.action.setEffectiveTimeScale(1);
			characterAnimation.action.reset();
			characterAnimation.action.play();
			characterAnimation.action.setEffectiveTimeScale(characterAnimation.playbackSpeed);
			characterAnimation.action.setLoop(THREE.LoopRepeat, loopTimes);
			characterAnimation.action.clampWhenFinished = true;

			if (fadeTimeMS > 0 && weight > 0) {
				this.fadeAnimation({ id: id, from: 0, to: weight, fadeTimeMS: fadeTimeMS });
			} else {
				characterAnimation.action.weight = weight;
			}

			if (characterAnimation.reversedAction) {
				characterAnimation.reversedAction.weight = 0;
				characterAnimation.reversedAction.reset();
				characterAnimation.reversedAction.play();
				characterAnimation.reversedAction.setLoop(THREE.LoopRepeat, loopTimes);
				characterAnimation.reversedAction.setEffectiveTimeScale(-1 * characterAnimation.playbackSpeed);
			}

			this.characterActiveAnimations.push(characterAnimation);
			return new BridgeActionResult(true);
		} catch(e) {
			return new BridgeActionResult(false, BridgeErrorStrings.activate_animation_default);
		}
	}

	/**
	 * Deactivates both an animation and its reversed counterpart
	 * @param animationTitle string, name of the animation
	 * @param weight Number, the weight of the animation
	 */
	deactivateAnimation({ id, fadeTimeMS = 0 }: AnimationDeactivationInterface): BridgeActionResult {
		const characterAnimation = this.getAnimationByID(id);
		if (!characterAnimation) return new BridgeActionResult(false, BridgeErrorStrings.no_asset);
			
		try {
			characterAnimation.action.setEffectiveTimeScale(characterAnimation.action.timeScale * 0.15);
			characterAnimation.autoBlendTriggered = true; // Do not trigger another autoblend

			if (fadeTimeMS > 0) {
				this.fadeAnimation({ id: id, from: characterAnimation.action.weight, to: 0, onComplete: () => allStop(characterAnimation) });
				if (characterAnimation.reversedAction) this.fadeAnimation({ id: id, actionSelector: "reversedAction", from: characterAnimation.reversedAction.weight, to: 0, onComplete: () => allStop(characterAnimation) });
			} else {
				allStop(characterAnimation);
			}

			this.characterActiveAnimations = this.characterActiveAnimations.filter(anim => anim.id !== id);
			return new BridgeActionResult(true);
		} catch(e) {
			return new BridgeActionResult(false, BridgeErrorStrings.deactivate_animation_default);
		}

		// Callback to finally stop the animation
		function allStop(characterAnimation: CharacterAnimationDefinition) {
			characterAnimation.action.weight = 0;
			characterAnimation.action.stop();

			if (characterAnimation.reversedAction) {
				characterAnimation.reversedAction.weight = 0;
				characterAnimation.reversedAction.stop();
			}
		}
	}

	/**
	 * Returns a characterAnimation by name
	 * @param animationTitle The name of the animation
	 * @returns A CharacterAnimationDefinition or undefined
	 */
	getAnimationByID(animationID: string): CharacterAnimationDefinition | undefined {
		return this.characterAnimations.find(anim => anim.id == animationID);
	}

	/**
	 * Takes an animation and reverses it in order to blend it in and out as the animation approaches the end
	 * @param characterAnimation The animation to reverse
	 */
	createReversedClipAndAction(characterAnimation: CharacterAnimationDefinition) {
		const reversedClipJSON = JSON.stringify(characterAnimation.clip);
		const reversedClip = JSON.parse(reversedClipJSON);
		reversedClip.name += this.reversedIdAddon;
		reversedClip.uuid = generateUUID();
		const threeClip = THREE.AnimationClip.parse(reversedClip);
		const reversedAction = this.characterAnimationMixer?.clipAction(threeClip)!;
		reversedAction.setLoop(THREE.LoopOnce, 1);
		reversedAction.clampWhenFinished = true;
		reversedAction.time = threeClip.duration;

		return {
			reversedClip: reversedClip,
			reversedAction: reversedAction
		}
	}

	/**
	 * Reduces the animation Clip's intensity via Tween
	 * Called only by internal tools.  Use activate and deactivate to enable and disable animations
	 */
	private fadeAnimation({ id, actionSelector = "action", from, to, fadeTimeMS, onComplete }: AnimationFadeInterface) {
		const characterAnimation = this.characterAnimations.find(anim => anim.id == id);
		if (!characterAnimation || !characterAnimation[actionSelector]) return false;

		characterAnimation[actionSelector].weight = from;
		const sTween: TweenService = this.core.s('Tween');
		const animationName = characterAnimation.id + Math.ceil(Math.random() * 1000);
		sTween.createTween({
			name: `fade_${animationName}`,
			targetObject: characterAnimation[actionSelector],
			modifiedPropertyObject: { weight: to },
			durationMS: fadeTimeMS || 1000,
			easing: TWEEN.Easing.Cubic.InOut,
			onComplete: () => onComplete && onComplete()
		});
	}

	/**
	 * Called by Tick to animate the mixer
	 * @param time Time, provided by Tick Service
	 */
	characterAnimationTick(time: TickTimeProperties) {
		this.characterAnimationMixer?.update(time.deltaTime);

		this.characterActiveAnimations.forEach((activeAnimation: CharacterAnimationDefinition) => {
			if (activeAnimation.autoBlend && !activeAnimation.autoBlendTriggered) {
				//const actionEffectiveTimeScale = activeAnimation.action.getEffectiveTimeScale(); 
				const actionElapsedTimeMS = activeAnimation.action.time * 1000;
				const clipDurationMS = activeAnimation.clip.duration * 1000;
				const approachTime = clipDurationMS - (this.reverseAnimationApproachTimeMS * activeAnimation.playbackSpeed);
				if (actionElapsedTimeMS > approachTime) {
					this.$progressEvents.next({ type: AnimationProgressEventType.approaching, animationAction: activeAnimation.action })
					activeAnimation.autoBlendTriggered = true;
				}
			}
		});
	}
}